import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const TOKEN_KEY = "vu_token";

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    // Never let a stalled network wipe user state — the auth layer treats
    // aborts/timeouts as "keep the user optimistic, retry later" (offline
    // PWA startup, roaming, sleeping laptop, etc.).
    timeout: 30000,
});

// Send Bearer token as fallback when cookies are blocked (cross-domain previews etc.)
// Also bridge the founder-only "View As" mode to the backend: the header is
// only ever *read* by the backend when the caller is actually a founder
// (see `core/security.py::_attach_view_as`) — so sending it always is
// safe for non-founders too, but we scope the send strictly to founders'
// browsers by only attaching it when the localStorage key exists.
api.interceptors.request.use((config) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) config.headers.Authorization = `Bearer ${t}`;
    try {
        const viewAs = localStorage.getItem("vu_founder_view_as");
        if (viewAs === "professional" || viewAs === "student") {
            config.headers["X-View-As"] = viewAs;
        }
    } catch { /* private mode / SSR */ }
    return config;
});

// Subscribers for global paywall/verification handling
const paywallListeners = new Set();
export function onPaywall(fn) {
    paywallListeners.add(fn);
    return () => paywallListeners.delete(fn);
}

api.interceptors.response.use(
    (r) => {
        // Sliding session — the backend rotates the JWT on active users and
        // exposes it via `X-Auth-Token`. Persist it so bearer-only clients
        // (mobile, cross-domain previews) keep sliding forward too.
        const rotated = r?.headers?.["x-auth-token"] || r?.headers?.["X-Auth-Token"];
        if (rotated) localStorage.setItem(TOKEN_KEY, rotated);
        return r;
    },
    (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = detail && typeof detail === "object" ? detail.code : null;
        if ((status === 402 && code === "subscription_required") ||
            (status === 403 && code === "email_verification_required")) {
            paywallListeners.forEach((fn) => fn(detail));
        }
        return Promise.reject(err);
    },
);

// Distinguish "server explicitly rejected the token" from "we couldn't
// even reach the server" (offline, timeout, DNS, 5xx). Callers use this
// to decide whether to clear tokens or keep the user optimistic.
export function isAuthRejection(err) {
    return err?.response?.status === 401;
}

// True when the error is purely a transport failure — no HTTP status at
// all. Common during PWA cold starts on flaky networks; must NEVER cause
// a logout.
export function isNetworkError(err) {
    return !err?.response;
}

export function saveToken(token) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
}
export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}

export function formatErr(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Async upload + processing. Uploads via /upload-jobs (fast — file lands
// in object storage and the backend enqueues a background task), then
// polls GET /upload-jobs/{job_id} every 2 s until status is `complete`
// or `failed`. Emits onStatus updates for the UI:
//
//   onStatus("uploading",  { percent })
//   onStatus("processing", { attempt })
//   onStatus("complete",   { result })
//   onStatus("failed",     { error, friendly })
//
// Returns the final result payload on success. Throws with `.friendly`
// on failure so call-sites can `catch (e) { showError(e.friendly) }`.
//
// Options:
//   files          — single File OR array of File objects
//   kind           — "document_extract" | "lecture_extract" | "audio_transcribe"
//   onStatus       — (stage, extra) => void
//   pollInterval   — ms between poll attempts (default 2000)
//   maxWait        — ms cap for the whole processing phase (default 2 h)
export async function uploadAndProcess({ files, kind, onStatus, pollInterval = 2000, maxWait = 2 * 60 * 60 * 1000 }) {
    const list = Array.isArray(files) ? files : [files];
    if (!list.length) throw new Error("No files");

    onStatus?.("uploading", { percent: 0 });
    const fd = new FormData();
    fd.append("kind", kind);
    for (const f of list) fd.append("files", f, f.name);

    // Stage 1: upload. Fast — returns job_id in a couple of seconds.
    const job = await uploadFile("/upload-jobs", fd, {
        onProgress: (percent) => onStatus?.("uploading", { percent }),
    });
    if (!job?.job_id) {
        const err = new Error("Server did not return a job id.");
        err.friendly = "The server responded oddly. Please try again.";
        throw err;
    }

    // Stage 2: poll. `processing` → `complete` | `failed`.
    onStatus?.("processing", { attempt: 0 });
    const started = Date.now();
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        attempt += 1;
        if (Date.now() - started > maxWait) {
            const err = new Error("Processing took too long");
            err.friendly = "Processing is taking longer than expected. It may finish shortly — refresh in a minute to check.";
            onStatus?.("failed", { error: err.message, friendly: err.friendly });
            throw err;
        }
        // Small wait BEFORE first poll — gives the background task a
        // moment to start; avoids a tight round-trip when files are tiny.
        await new Promise((r) => setTimeout(r, pollInterval));
        let status;
        try {
            const { data } = await api.get(`/upload-jobs/${job.job_id}`);
            status = data;
        } catch (err) {
            // Transient network hiccup during polling — keep trying up
            // to the max-wait ceiling; a hard 401/404 breaks out.
            if (err?.response?.status === 404) {
                const e = new Error("Job vanished");
                e.friendly = "The processing job could not be found. Please try uploading again.";
                onStatus?.("failed", { error: e.message, friendly: e.friendly });
                throw e;
            }
            if (err?.response?.status === 401) {
                err.friendly = "Your session has expired. Please sign in again.";
                onStatus?.("failed", { error: err.message, friendly: err.friendly });
                throw err;
            }
            // eslint-disable-next-line no-console
            console.warn("[upload-job] poll transient error", err?.message);
            onStatus?.("processing", { attempt });
            continue;
        }

        if (status?.status === "complete") {
            onStatus?.("complete", { result: status.result });
            return status.result;
        }
        if (status?.status === "failed") {
            const err = new Error(status.error || "Processing failed");
            err.friendly = status.error || "Processing failed. Please try again.";
            onStatus?.("failed", { error: err.message, friendly: err.friendly });
            throw err;
        }
        onStatus?.("processing", { attempt });
    }
}
// Upload helper.
// Uses a *longer* dedicated timeout (2 h) instead of the 30 s global default —
// large files (meeting audios, lecture PowerPoints) legitimately take that
// long over slow mobile networks, and the async `/upload-jobs` polling flow
// relies on the initial POST + subsequent GETs all being able to wait
// through the whole processing window. Returns the parsed response body on
// success and throws a wrapped Error with a human-readable `.friendly`
// message on failure so call-sites don't have to pattern-match axios
// internals.
//
// Options:
//   - onProgress(percent, {loaded, total}) — 0–100 upload progress
//   - signal — AbortSignal to cancel from the UI
//   - timeout — override default (default 7200000 = 2 h)
export async function uploadFile(url, formData, opts = {}) {
    const { onProgress, signal, timeout = 2 * 60 * 60 * 1000 } = opts;
    try {
        const r = await api.post(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout,
            signal,
            onUploadProgress: (evt) => {
                if (!onProgress) return;
                const total = evt.total || evt.loaded;
                if (!total) return;
                const percent = Math.round((evt.loaded / total) * 100);
                onProgress(percent, { loaded: evt.loaded, total });
            },
        });
        return r.data;
    } catch (err) {
        // Build a friendly, actionable message. Order matters: check
        // AbortError first (user-triggered cancel), then timeout, then
        // network error, then status-code specific messages.
        let friendly;
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
            friendly = "Upload cancelled.";
        } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
            friendly = "Upload took too long. Check your connection and try again — large files can take a few minutes on slower networks.";
        } else if (isNetworkError(err)) {
            friendly = "Could not reach the server. Check your internet connection and try again.";
        } else if (err?.response?.status === 413) {
            friendly = err?.response?.data?.detail || "File too large. Try a smaller file (max 25 MB).";
        } else if (err?.response?.status === 415) {
            friendly = "File type not supported.";
        } else if (err?.response?.status === 401) {
            friendly = "Your session has expired. Please sign in again.";
        } else if (err?.response?.status === 429) {
            friendly = "Too many uploads in a short window. Please wait a moment and try again.";
        } else if (err?.response?.status >= 500) {
            friendly = "The upload service is temporarily unavailable. Please try again in a moment.";
        } else {
            friendly = formatErr(err?.response?.data?.detail) || "Upload failed. Please try again.";
        }
        // Attach so callers can `catch (e) { showError(e.friendly) }`.
        err.friendly = friendly;
        // Console breadcrumb — helps ops diagnose reports of "upload just
        // failed" without asking the user to open devtools.
        // eslint-disable-next-line no-console
        console.warn("[upload]", url, {
            status: err?.response?.status,
            code: err?.code,
            message: err?.message,
            friendly,
        });
        throw err;
    }
}
